import React from 'react';
import {API} from "aws-amplify";
import {toast, ToastContainer} from "react-toastify";
import Select from "react-select";
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import _ from "lodash";

import InputCell from "@/common/form/InputCell";

const MAX_RULE_NAME_LENGTH = 255;

const focusToastCloseButton = () => {
    // Focus the close button after toast renders
    // Use multiple attempts with different selectors to handle various toast versions
    const attemptFocus = (attempts = 0) => {
        if (attempts > 10) return; // Give up after 1 second

        // Try multiple selectors for the close button
        const selectors = [
            '#modal-toast .Toastify__close-button',  // Modal-specific toast container
            '.Toastify__toast--top-center .Toastify__close-button',  // Top-center positioned toast
            '.Toastify__toast:last-child .Toastify__close-button',  // Most recent toast
            '.Toastify__close-button'  // Fallback to any close button
        ];

        for (const selector of selectors) {
            const closeButton = document.querySelector(selector);
            if (closeButton && closeButton.offsetParent !== null) {  // Check if visible
                closeButton.focus();
                console.log('Toast close button focused with selector:', selector);
                return;
            }
        }

        // If not found, try again after a short delay
        setTimeout(() => attemptFocus(attempts + 1), 100);
    };

    attemptFocus();
};

// EN-21359: react-toastify announces a toast through both its container (aria-live="polite")
// and the toast itself (role="alert"), and moving focus to the close button (EN-21346) makes the
// screen reader read the toast a third time. Focus is the one announcement we keep: the toast is
// a labelled group, so focusing its close button reads the message exactly once.
const modalToastOptions = (message) => ({
    containerId: "modal-toast",
    position: "top-center",
    hideProgressBar: true,  // Progress bar is announced as "notification timer"
    role: "group",
    ariaLabel: message,
    onOpen: focusToastCloseButton
});

function ModalToastContainer() {
    const wrapperRef = React.useRef(null);

    // react-toastify hardcodes aria-live="polite" on its container with no prop to override it.
    // React only rewrites the attribute if the value changes, so setting it once on mount sticks.
    React.useEffect(() => {
        wrapperRef.current?.querySelector('.Toastify')?.setAttribute('aria-live', 'off');
    }, []);

    return (
        <div ref={wrapperRef}>
            <ToastContainer containerId="modal-toast" position="top-center" theme="dark" />
        </div>
    );
}

export default function AddEditRule(props){
    const { onCancel, onSuccess, existingRule, rules} = props;

    const titleRef = React.useRef(null);

    const [step, setStep] = React.useState(1);
    const [mode, setMode] = React.useState('scratch');
    const [selectedRule, setSelectedRule] = React.useState(null);
    const [selectedVersion, setSelectedVersion] = React.useState(null);
    const [versions, setVersions] = React.useState([]);
    const [loadingVersions, setLoadingVersions] = React.useState(false);
    const [ruleName, setRuleName] = React.useState('');
    const [activeVersionId, setActiveVersionId] = React.useState(null);

    // EN-21346: Add visual focus indicator for toast close button (508c compliance)
    React.useEffect(() => {
        const style = document.createElement('style');
        style.textContent = '.Toastify__close-button:focus-visible { outline: 3px solid #005fcc; outline-offset: 2px; border-radius: 2px; }';
        document.head.appendChild(style);
        return () => {
            document.head.removeChild(style);
        };
    }, []);

    const showError = (message) => {
        toast(message, {
            ...modalToastOptions(message),
            type: "error",
            autoClose: 5000,  // Auto-close after 5 seconds (user can still dismiss early)
            closeOnClick: false,  // Require explicit close button click for accessibility
            draggable: false  // Prevent accidental dismissal
        });
    };

    const handleNext = () => {
        const name = titleRef.current.value.trim();
        if (!name) {
            showError("Rule name cannot be empty");
            return;
        }
        if (name.length > MAX_RULE_NAME_LENGTH) {
            showError(`Rule name limit is ${MAX_RULE_NAME_LENGTH} characters`);
            return;
        }
        setRuleName(name);
        setStep(2);
    };

    const handleBack = () => {
        setStep(1);
    };

    const handleRuleChange = (option) => {
        setSelectedRule(option);
        setSelectedVersion(null);
        setVersions([]);
        setActiveVersionId(null);
        if (!option) {
            return;
        }
        setLoadingVersions(true);

        // Fetch both the rule details (for activeRuleVersion) and versions in parallel
        Promise.all([
            API.get('Core', `/api/v1/rule/${option.value}`),
            API.get('Core', `/api/v1/rule/${option.value}/version`)
        ])
            .then(([ruleDetails, versionsResponse]) => {
                setVersions(versionsResponse);

                // Find active version by comparing with rule's activeRuleVersion
                const activeVersionIdFromRule = ruleDetails.activeRuleVersion?.id;
                setActiveVersionId(activeVersionIdFromRule);

                if (activeVersionIdFromRule) {
                    const activeVersion = versionsResponse.find(v => v.id === activeVersionIdFromRule);
                    if (activeVersion) {
                        setSelectedVersion({
                            value: activeVersion.id,
                            label: `Version ${activeVersion.version} (Active)`
                        });
                    }
                }
                setLoadingVersions(false);
            })
            .catch(error => {
                showError("Could not load versions");
                setVersions([]);
                setLoadingVersions(false);
            });
    };

    const handleCreate = (nameOverride) => {
        // If nameOverride is an event object (has nativeEvent), ignore it and use ruleName
        const name = (nameOverride && !nameOverride.nativeEvent) ? nameOverride : ruleName;
        if (mode === 'scratch') {
            API.post('Core', '/api/v1/rule', {body: {name}})
                .then(response => {
                    toast("Rule Saved!", {
                        ...modalToastOptions("Rule Saved!"),
                        type: "success",
                        autoClose: 2000,  // Auto-close after 2 seconds
                        onClose: () => {
                            // Close modal and refresh after toast is dismissed
                            onSuccess(response);
                        }
                    });
                },
                error => {
                    showError("Could not create rule: " + error.response.data.message);
                });
        } else {
            API.post('Core', '/api/v1/rule/copy', {
                body: {name, sourceRuleVersionId: selectedVersion.value}
            })
                .then(response => {
                    toast("Rule Saved!", {
                        ...modalToastOptions("Rule Saved!"),
                        type: "success",
                        autoClose: 2000,  // Auto-close after 2 seconds
                        onClose: () => {
                            // Close modal and refresh after toast is dismissed
                            onSuccess(response);
                        }
                    });
                },
                error => {
                    showError("Could not create rule: " + error.response.data.message);
                });
        }
    };

    const saveService = () => {
        const name = titleRef.current.value.trim();
        if (!name) {
            showError("Rule name cannot be empty");
            return;
        }
        if (name.length > MAX_RULE_NAME_LENGTH) {
            showError(`Rule name limit is ${MAX_RULE_NAME_LENGTH} characters`);
            return;
        }
        API.put('Core', '/api/v1/rule/' + existingRule.id, {body: {name: name}})
            .then(response => {
                toast("Rule Saved!", {
                    ...modalToastOptions("Rule Saved!"),
                    type: "success",
                    autoClose: 2000,  // Auto-close after 2 seconds
                    onClose: () => {
                        // Close modal and refresh after toast is dismissed
                        onSuccess(response);
                    }
                });
            },
            error => {
                showError("Could not update rule: " + error.response.data.message);
            });
    };

    const isCreateDisabled = mode === 'copy' && (!selectedRule || !selectedVersion);

    const ruleOptions = (rules || []).map(r => ({
        value: r.id,
        label: r.name + (r.status === 'DISABLED' ? ' (Inactive)' : '')
    }));

    // Sort versions from newest to oldest (descending by version number)
    const sortedVersions = _.reverse(_.sortBy(versions, v => v.version));
    const versionOptions = sortedVersions.map(v => ({
        value: v.id,
        label: `Version ${v.version}${v.id === activeVersionId ? ' (Active)' : ''}`
    }));

    // Custom styles for react-select to match theme and appear above modal
    const selectStyles = {
        control: (base) => ({
            ...base,
            backgroundColor: 'var(--colorBackgroundPrimary)',
            borderColor: 'var(--colorSeparator)',
            '&:hover': {
                borderColor: 'var(--colorSeparator)',
            },
        }),
        input: (base) => ({
            ...base,
            color: 'var(--colorTitle)',
        }),
        singleValue: (base) => ({
            ...base,
            color: 'var(--colorTitle)',
        }),
        placeholder: (base) => ({
            ...base,
            color: 'var(--colorTitlePlaceholder)',
        }),
        menuPortal: (base) => ({
            ...base,
            zIndex: 9999,
        }),
        menu: (base) => ({
            ...base,
            backgroundColor: 'var(--colorBackgroundPrimary)',
            borderColor: 'var(--colorSeparator)',
        }),
        menuList: (base) => ({
            ...base,
            backgroundColor: 'var(--colorBackgroundPrimary)',
            padding: 0,
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isFocused
                ? 'var(--colorTintTranslucent)'
                : state.isSelected
                    ? 'var(--colorTint)'
                    : 'var(--colorBackgroundPrimary)',
            color: state.isSelected ? 'var(--colorTitleSecondary)' : 'var(--colorTitle)',
            cursor: 'pointer',
            '&:active': {
                backgroundColor: 'var(--colorTint)',
            },
        }),
    };

    if (existingRule) {
        return (
            <div>
                <ModalToastContainer />
                <div className="content">
                    <div className="tableView">
                        <div className="tableSection sectionName">
                            <p style={{ fontSize: '0.75em', color: 'var(--colorTitle)', marginBottom: '6px', marginTop: '-4px' }}>
                                Fields marked with <span style={{ color: 'red' }}>*</span> are required.
                            </p>
                            <InputCell
                                placeholder={"Rule Name"}
                                defaultValue={existingRule.name}
                                required
                                id="ruleName"
                                label="Rule Name"
                                inputRef={titleRef}
                                maxLength={MAX_RULE_NAME_LENGTH}
                                showRequiredIndicator
                                blurErrorMessage="Rule Name cannot be empty."
                            />
                        </div>
                    </div>
                </div>
                <div className="actionBar">
                    <div className="actions">
                        <div
                            onClick={onCancel}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onCancel(); } }}
                            className="button dismiss medium"
                            role="button"
                            tabIndex={0}
                        >
                            <div className="title">Cancel</div>
                        </div>
                        <div
                            onClick={saveService}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); saveService(); } }}
                            className="button confirm medium"
                            role="button"
                            tabIndex={0}
                        >
                            <div className="title">Done</div>
                        </div>
                    </div>
                    <div className="separator"></div>
                </div>
            </div>
        );
    }


    if (step === 1) {
        return (
            <div>
                <ModalToastContainer />
                <div className="content">
                    <div className="tableView">
                        <div className="tableSection sectionName">
                            <p style={{ fontSize: '0.75em', color: 'var(--colorTitle)', marginBottom: '6px', marginTop: '-4px' }}>
                                Fields marked with <span style={{ color: 'red' }}>*</span> are required.
                            </p>
                            <InputCell
                                placeholder={"Rule Name"}
                                defaultValue={ruleName}
                                required
                                id="ruleName"
                                label="Rule Name"
                                inputRef={titleRef}
                                maxLength={MAX_RULE_NAME_LENGTH}
                                showRequiredIndicator
                                blurErrorMessage="Rule Name cannot be empty."
                            />
                        </div>
                    </div>
                </div>
                <div className="actionBar">
                    <div className="actions">
                        <div
                            onClick={onCancel}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onCancel(); } }}
                            className="button dismiss medium"
                            role="button"
                            tabIndex={0}
                        >
                            <div className="title">Cancel</div>
                        </div>
                        <div
                            onClick={handleNext}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleNext(); } }}
                            className="button confirm medium"
                            role="button"
                            tabIndex={0}
                        >
                            <div className="title">Next</div>
                        </div>
                    </div>
                    <div className="separator"></div>
                </div>
            </div>
        );
    }

    return (
        <div>
            <ModalToastContainer />
            <div className="content">
                <div className="tableView">
                    <div className="tableSection" style={{padding: '24px'}}>
                        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '28px'}}>
                            <div
                                onClick={() => setMode('scratch')}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setMode('scratch'); } }}
                                role="button"
                                tabIndex={0}
                                aria-pressed={mode === 'scratch'}
                                style={{
                                    border: mode === 'scratch' ? '2px solid var(--colorTint)' : '2px solid var(--colorSeparator)',
                                    borderRadius: '8px',
                                    padding: '32px 24px',
                                    cursor: 'pointer',
                                    backgroundColor: mode === 'scratch' ? 'var(--colorTintTranslucent)' : 'var(--colorBackgroundSecondary)',
                                    transition: 'all 0.2s ease',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    minHeight: '180px'
                                }}
                            >
                                <NoteAddIcon style={{fontSize: '56px', color: 'var(--colorSubtitle)', marginBottom: '16px'}} />
                                <div style={{fontSize: '17px', fontWeight: 500, color: 'var(--colorTitle)', marginBottom: '10px', lineHeight: '1.3'}}>
                                    Create from Scratch
                                </div>
                                <div style={{fontSize: '14px', color: 'var(--colorSubtitle)', lineHeight: '1.5'}}>
                                    Start with a blank rule
                                </div>
                            </div>

                            <div
                                onClick={() => setMode('copy')}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setMode('copy'); } }}
                                role="button"
                                tabIndex={0}
                                aria-pressed={mode === 'copy'}
                                style={{
                                    border: mode === 'copy' ? '2px solid var(--colorTint)' : '2px solid var(--colorSeparator)',
                                    borderRadius: '8px',
                                    padding: '32px 24px',
                                    cursor: 'pointer',
                                    backgroundColor: mode === 'copy' ? 'var(--colorTintTranslucent)' : 'var(--colorBackgroundSecondary)',
                                    transition: 'all 0.2s ease',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    minHeight: '180px'
                                }}
                            >
                                <FileCopyIcon style={{fontSize: '56px', color: 'var(--colorSubtitle)', marginBottom: '16px'}} />
                                <div style={{fontSize: '17px', fontWeight: 500, color: 'var(--colorTitle)', marginBottom: '10px', lineHeight: '1.3'}}>
                                    Copy Existing Rule
                                </div>
                                <div style={{fontSize: '14px', color: 'var(--colorSubtitle)', lineHeight: '1.5'}}>
                                    Duplicate a rule as a starting point
                                </div>
                            </div>
                        </div>

                        {mode === 'copy' && (
                            <>
                                <div style={{marginBottom: '16px'}}>
                                    <label style={{display: 'block', marginBottom: '6px', marginTop: '-4px', fontSize: '13px', fontWeight: 500, color: 'var(--colorSubtitle)'}}>
                                        Existing Rule
                                    </label>
                                    <Select
                                        className="selectCell"
                                        classNamePrefix="selectCell"
                                        options={ruleOptions}
                                        value={selectedRule}
                                        onChange={handleRuleChange}
                                        isClearable
                                        placeholder="- Select a rule -"
                                        isSearchable
                                        styles={selectStyles}
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                        maxMenuHeight={300}
                                        filterOption={(option, inputValue) => {
                                            if (!inputValue) return true;
                                            return option.label.toLowerCase().includes(inputValue.toLowerCase());
                                        }}
                                    />
                                </div>

                                <div>
                                    <label style={{display: 'block', marginBottom: '6px', marginTop: '-4px', fontSize: '13px', fontWeight: 500, color: 'var(--colorSubtitle)'}}>
                                        Version
                                    </label>
                                    <Select
                                        className="selectCell"
                                        classNamePrefix="selectCell"
                                        options={versionOptions}
                                        value={selectedVersion}
                                        onChange={setSelectedVersion}
                                        isDisabled={!selectedRule}
                                        isLoading={loadingVersions}
                                        placeholder="- Select a version -"
                                        styles={selectStyles}
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                        maxMenuHeight={300}
                                        filterOption={(option, inputValue) => {
                                            if (!inputValue) return true;
                                            return option.label.toLowerCase().includes(inputValue.toLowerCase());
                                        }}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
            <div className="actionBar">
                <div className="actions">
                    <div
                        onClick={handleBack}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleBack(); } }}
                        className="button dismiss medium"
                        role="button"
                        tabIndex={0}
                    >
                        <div className="title">Back</div>
                    </div>
                    <div
                        onClick={isCreateDisabled ? undefined : handleCreate}
                        onKeyDown={(e) => { if (!isCreateDisabled && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); handleCreate(); } }}
                        className={`button confirm medium ${isCreateDisabled ? 'disabled' : ''}`}
                        style={isCreateDisabled ? {opacity: 0.5, pointerEvents: 'none'} : {}}
                        role="button"
                        tabIndex={isCreateDisabled ? -1 : 0}
                        aria-disabled={isCreateDisabled}
                    >
                        <div className="title">Create Rule</div>
                    </div>
                </div>
                <div className="separator"></div>
            </div>
        </div>
    );
}
